import * as Res from "./res";
import { createElement, uniqueId } from "./_dom";
import { registerComponent } from "./component";
import { dbfetch } from "./dbfetch";

class Multiselect {
	constructor(select) {
		this.#select = select;
		this.#selectOptions = [...this.select.options];
		this.#listItems;
		this.#button;
		this.#popup;
	}

	itemsToSearch = 30;

	get select() {
		return this.#select;
	}

	get button() {
		return this.#button;
	}

	get popup() {
		return this.#popup;
	}

	get loading() {
		return this.#loadingSpan != null;
	}

	set loading(value) {
		if (value && !this.loading) {
			this.#loadingSpan = createElement("li", { class: "v3-menu__cell" }, createElement("div", { class: "v3-menu__label ui-icon-spinner v3-menu__label--disabled" }, String.format(Res.getString("form.load_detail"), "")));
			this.#listItems.append(this.#loadingSpan);
		} else if(!value && this.loading) {
			this.#loadingSpan.remove();
			this.#loadingSpan = null;
		}
	}

	getBlankText() {
		return Res.getString("form.choice.blank") || "--Blank--";
	}

	getButtonText() {
		let buttonText;
		const buttonLabel = this.#selectOptions
			.filter(option => option.selected === true)
			.map(option => option.text || this.getBlankText());

		const selectedCount = buttonLabel.length;
		const optionsCount = this.#selectOptions.length;

		if (selectedCount === 0) {
			buttonText = Res.getString("form.choice.any");
		} else if (selectedCount > 14) {
			buttonText = `${selectedCount} of ${optionsCount} selected`;
		} else {
			buttonText = buttonLabel.sort().join(", ");
		}
		return buttonText;
	}

	getItems() {
		return this.#selectOptions.map(option => this.createItem(option));
	}

	onItemClick = ({ target }) => {
		const input = target.closest("input");
		if (!input) return;
		const checked = input.checked;
		const option = this.#selectOptions.find(option => option.value === input.value);
		option.selected = checked;
		this.updateButtonText();
	};

	updateButtonText() {
		this.#button.replaceChildren(this.getButtonText());
	}

	updateItemsList() {
		this.#selectOptions = [...this.select.options];
		const newListItems = this.createItemList();
		this.#listItems.replaceWith(newListItems);
		this.#listItems = newListItems;
	}

	refresh() {
		this.#loadingSpan = null;
		this.updateItemsList();
		this.updateSearchInput();
		this.updateButtonText();
	}

	onClickSelectAllNoneButton = ({ target }) => {
		target = target.closest("button");
		this.#selectOptions.forEach(option => {
			if (!option.dataset.hidden) option.selected = target.dataset.allSelected;
		});
		this.refresh();
	};

	onSearchInput = ({ target: input }) => {
		const inputValue = input.value.toLowerCase();
		const listForSearch = Array.from(this.#listItems.querySelectorAll('.v3-multiselect__item'));
		for (let i = 0; i < listForSearch.length; i++) {
			const itemValue = listForSearch[i]
				.querySelector("span")
				.textContent
				.toLowerCase();
			if (itemValue.indexOf(inputValue) >= 0) {
				listForSearch[i].style.display = "";
				this.#selectOptions[i].dataset.hidden = "";
			} else {
				listForSearch[i].style.display = "none";
				this.#selectOptions[i].dataset.hidden = true;
			}
		}
	};

	// create elements multiselect
	createButton(popupId) {
		const btn = createElement("button", {
				id: uniqueId("multiselect-button"),
				class: "v3-multiselect__drop v3-select js-auto-popup",
				type: "button",
				"aria-haspopup": "true",
				"aria-controls": popupId
			});
		return btn;
	}

	updateSearchInput() {
		const popupHead = this.#popup.querySelector(".v3-multiselect__head");
		let searchInput = popupHead.querySelector(".v3-multiselect__search");
		if (searchInput && this.#selectOptions.length < this.itemsToSearch) {
			searchInput.remove();
		} else if (!searchInput && this.#selectOptions.length >= this.itemsToSearch) {
			searchInput = createElement("input", {
				class: "v3-multiselect__search v3-input",
				type: "text",
				placeholder: Res.getString("dbui.search.button"),
				oninput: this.onSearchInput
			});
			popupHead.prepend(searchInput);
		}
	}

	createPopupHead() {
		const btnSelectedAll = createElement("button", {
				dataset: { allSelected: true },
				class: "v3-multiselect__button v3-button",
				type: "button",
				onclick: this.onClickSelectAllNoneButton
			},
			Res.getString("form.choice.select_all"));

		const btnSelectedNo = createElement("button", {
				class: "v3-multiselect__button v3-button",
				type: "button",
				onclick: this.onClickSelectAllNoneButton
			},
			Res.getString("form.choice.select_none"));

		const popupHead = createElement("div", { class: "v3-multiselect__head" },
			btnSelectedAll,
			btnSelectedNo);
		return popupHead;
	}

	createItem(option) {
		const { text, value, selected: checked, disabled, dataset: { hidden } } = option;
		const checkbox = createElement("input", {
			class: "v3-multiselect__checkbox v3-input-checkbox",
			value,
			type: "checkbox",
			checked,
			disabled
		});
		const textStr = text || this.getBlankText();
		const textSpan = createElement("span", { class: 'v3-multiselect__text v3-menu__text' }, textStr);
		const itemLabel = createElement("label", { role: "menuitemcheckbox", class: ['v3-multiselect__item v3-menu__item', disabled && 'v3-menu__item--disabled'] }, checkbox, textSpan);
		const item = createElement("li", {
				role: "none",
				class: "v3-multiselect__cell v3-menu__cell",
				onclick: this.onItemClick
			},
			itemLabel
		);
		item.style.display = hidden ? "none" : '';
		return item;
	}

	createItemList() {
		const list = createElement("ul", {
				role: "menu",
				class: "v3-multiselect__list v3-menu v3-menu-scrollable"
			},
			...this.getItems()
		);
		return list;
	}

	createPopup(popupId) {
		const popup = createElement("div", {
				id: popupId,
				class: "v3-multiselect__popup v3-popup",
				"data-popup-autoheight": ""
			},
			this.createPopupHead(),
			this.#listItems = this.createItemList()
		);
		return popup;
	}

	createFauxSelect() {
		const popupId = uniqueId("v3-multiselect-dropdown");
		this.#button = this.createButton(popupId);
		this.#popup = this.createPopup(popupId);
		this.updateButtonText();
		this.updateSearchInput();
		const fauxSelect = createElement("div", { class: "v3-multiselect" }, this.#button, this.#popup);
		this.select.after(fauxSelect);
	}

	init() {
		this.select.style.display = "none";
		this.createFauxSelect();
	}

	/** @type {HTMLSelectElement}*/#select;
	/** @type {HTMLUListElement}*/#listItems;
	/** @type {HTMLButtonElement}*/#button;
	/** @type {HTMLDivElement}*/#popup;
	/** @type {HTMLLIElement}*/#loadingSpan;
	/** @type {HTMLOptionElement[]}*/#selectOptions;
}

function createMultiselect(select) {
	const newSelect = new Multiselect(select);
	newSelect.init();
	return newSelect;
}

const multiselect = registerComponent(createMultiselect);

function init(context, selector = "select[multiple].js-multichoice") {
	multiselect(selector, context).forEach((/**@type {Multiselect}*/ms) => {
		ms.button.addEventListener("v3-popup.beforeopen", async () => {
			const select = ms.select;
			if (select.dataset.url) {
				try {
					ms.loading = true;
					const r = await dbfetch(select.dataset.url);
					if (r.ok) {
						const data = await r.json();
						ms.loading = false;
						select.replaceChildren(...data.map(i => new Option(i.text, i.value, i.selected, i.selected)));
						delete select.dataset.url;
						ms.refresh();
					}
				} finally {
					ms.loading = false;
				}
			}
		});
	});
}

export { multiselect, init };